import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ArrowUpRight, MapPin, Languages } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { COACHES } from "@/data/coaches";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { useCoachVerifications } from "@/lib/useCoachVerifications";
import { useCoachLanguages } from "@/lib/useCoachLanguages";
import { LanguageFilter, ALL_LANGUAGES } from "@/components/LanguageFilter";
import { useState } from "react";
import { useCoachVideos } from "@/lib/useCoachVideos";
import { CoachIntroVideo } from "@/components/CoachIntroVideo";

const PAGE_META = {
  title: "San Francisco Golf Coaches | Find a Golf Coach | GolfStart",
  description:
    "Find golf coaches in San Francisco for beginners, juniors, teens, and adults. Compare coaching styles, lesson options, locations, and pricing with GolfStart.",
};


// Hidden coaches stay in code; shown again by removing `hidden`.
const VISIBLE_COACHES = COACHES.filter((c) => !c.hidden);

function CoachCard({ coach, verified, languages = [], videoId }) {
  return (
    <article
      data-testid={`coach-card-${coach.id}`}
      className="group relative flex flex-col bg-cream-light border border-cream-border overflow-hidden transition-transform duration-500 ease-out hover:-translate-y-1 hover:border-gold"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-cream border-b border-cream-border">
        <img
          src={coach.image}
          alt={`${coach.name} — ${coach.role}`}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
        />
        <div className="absolute top-4 right-4 bg-cream-light/95 backdrop-blur px-3 py-1 font-sans text-[10px] tracking-[0.25em] uppercase text-forest border border-cream-border">
          from&nbsp;${coach.priceFrom}
        </div>
      </div>

      <div className="flex flex-1 flex-col p-7">
        <div className="eyebrow">{coach.role}</div>
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
          <h3 className="font-serif text-3xl md:text-[2rem] leading-tight text-forest">
            {coach.name}
          </h3>
          {verified && <VerifiedBadge testId={`coach-verified-badge-${coach.id}`} />}
        </div>

        <p className="mt-4 font-sans text-sm leading-relaxed text-forest-muted">
          {coach.intro}
        </p>

        <div className="mt-5 flex flex-wrap gap-2">
          {coach.specialties.slice(0, 4).map((s) => (
            <span
              key={s}
              className="border border-cream-border bg-cream px-3 py-1 text-[10px] tracking-[0.2em] uppercase text-forest"
            >
              {s}
            </span>
          ))}
        </div>

        {languages.length > 0 && (
          <div
            data-testid={`coach-languages-${coach.id}`}
            className="mt-4 flex items-start gap-2 font-sans text-xs leading-relaxed text-forest-muted"
          >
            <Languages className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" strokeWidth={1.5} />
            <span>
              <span className="tracking-[0.2em] uppercase text-[10px] text-forest">Teaches in</span>{" "}
              {languages.join(" · ")}
            </span>
          </div>
        )}

        {videoId && <CoachIntroVideo coachId={coach.id} coachName={coach.name} videoId={videoId} />}

        <div className="mt-6 pt-5 border-t border-cream-border flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-xs text-forest-muted">
            <MapPin className="h-3.5 w-3.5 text-gold" strokeWidth={1.5} />
            San Francisco, CA
          </div>
          <a
            href={coach.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`coach-book-btn-${coach.id}`}
            className="btn-primary inline-flex items-center gap-2 !px-5 !py-3 text-[10px]"
          >
            View Profile
            <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.75} />
          </a>
        </div>
      </div>
    </article>
  );
}

export default function CoachList() {
  useSeo(PAGE_META);
  const verifications = useCoachVerifications();
  const coachLanguages = useCoachLanguages();
  const coachVideos = useCoachVideos();
  const [language, setLanguage] = useState(ALL_LANGUAGES);
  const shown = VISIBLE_COACHES.filter(
    (c) => language === ALL_LANGUAGES || (coachLanguages[c.id] || []).includes(language),
  );
  return (
    <div className="min-h-screen bg-cream" data-testid="coach-list-page">
      <Header />

      {/* Hero */}
      <section className="relative border-b border-cream-border">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <div className="grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-12">
              <div className="eyebrow mb-6">Find a Coach — San Francisco</div>
              <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.95] text-forest">
                Find a Golf Coach <em className="italic text-gold font-light">in San Francisco.</em>
              </h1>
              <p className="mt-8 max-w-xl font-sans text-base md:text-lg leading-relaxed text-forest-muted">
                Find experienced golf coaches in San Francisco for beginners, juniors, teens,
                and adults. Compare coaching styles, lesson options, locations, and pricing to
                find the right coach for your game.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Directory grid */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-16 md:py-24">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="eyebrow" data-testid="coach-count">
            {shown.length} {shown.length === 1 ? "Coach" : "Coaches"}
          </div>
          <LanguageFilter value={language} onChange={setLanguage} />
        </div>
        {shown.length === 0 ? (
          <p
            data-testid="language-filter-empty"
            className="border border-cream-border bg-cream-light px-6 py-12 text-center font-serif text-xl italic text-forest-muted"
          >
            No coaches are currently available for this language.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
            {shown.map((c) => (
              <CoachCard
                key={c.id}
                coach={c}
                verified={verifications[c.id] === "verified"}
                languages={coachLanguages[c.id] || []}
                videoId={coachVideos[c.id]}
              />
            ))}
          </div>
        )}
      </section>

      {/* SEO editorial */}
      <section
        data-testid="coach-seo-section"
        className="border-t border-cream-border bg-cream-light"
      >
        <div className="mx-auto max-w-[1100px] px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <div className="max-w-2xl">
            <div className="eyebrow mb-6">A Guide to Coaching in San Francisco</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] text-forest">
              Golf lessons for every level{" "}
              <em className="italic text-gold font-light">in San Francisco.</em>
            </h2>
            <p className="mt-6 font-sans text-base md:text-lg leading-relaxed text-forest-muted">
              GolfStart helps you find a San Francisco golf coach based on what actually matters
              to you — your experience level, goals, location, budget and preferred teaching
              style. Below is a short guide to help you narrow the roster.
            </p>
          </div>

          <div className="mt-16 grid md:grid-cols-2 gap-x-14 gap-y-14">
            <article>
              <h3 className="font-serif text-2xl md:text-3xl text-forest leading-tight">
                Beginner golf lessons
              </h3>
              <p className="mt-4 font-sans text-base leading-relaxed text-forest-muted">
                If you&rsquo;re new to the game, look for a coach who focuses on fundamentals —
                grip, posture, alignment and a repeatable swing. Beginner golf lessons work best
                when they emphasise confidence and let you learn at a comfortable pace, rather
                than rushing straight to full-swing power.
              </p>
            </article>

            <article>
              <h3 className="font-serif text-2xl md:text-3xl text-forest leading-tight">
                Junior golf lessons
              </h3>
              <p className="mt-4 font-sans text-base leading-relaxed text-forest-muted">
                Choosing a coach for a junior golfer is different from choosing one for
                yourself. Consider the coach&rsquo;s experience teaching young players, their
                teaching style, lesson format, location and qualifications. A patient,
                age-appropriate approach matters more than any technical resume.
              </p>
            </article>

            <article>
              <h3 className="font-serif text-2xl md:text-3xl text-forest leading-tight">
                Adult golf lessons
              </h3>
              <p className="mt-4 font-sans text-base leading-relaxed text-forest-muted">
                Adults come to lessons with different goals. Some are learning from scratch;
                others want to sharpen a specific part of their game — short game, putting,
                course strategy or driving. Match the coach&rsquo;s specialty to the outcome
                you&rsquo;re after, and you&rsquo;ll see progress faster.
              </p>
            </article>

            <article>
              <h3 className="font-serif text-2xl md:text-3xl text-forest leading-tight">
                How to choose a golf coach in San Francisco
              </h3>
              <p className="mt-4 font-sans text-base leading-relaxed text-forest-muted">
                When comparing San Francisco golf coaches, weigh five things: coaching
                experience, teaching style, lesson pricing, location and availability, and any
                specialties that fit your goals. Reading each profile carefully — and starting
                with a single lesson before committing to a package — is the surest way to find
                the right fit.
              </p>
              <Link
                to="/journal/how-to-choose-a-golf-coach"
                data-testid="coach-seo-journal-link"
                className="mt-5 inline-flex items-center gap-2 font-sans text-xs tracking-[0.25em] uppercase text-forest-dark hover:text-gold transition-colors"
              >
                Read the full guide in the Journal
                <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.75} />
              </Link>
            </article>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
